/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Header Component with Theme, Language & Super Admin Profile (Task 11.1.3 / Task 4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { LogOut } from 'lucide-react';
import { SophiaIcon } from '@sofia/assets';
import { useI18n } from '../../i18n';
import { useAdminAuthStore } from '../../stores/useAdminAuthStore';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { ThemeToggle } from '../common/ThemeToggle';
import { ADMIN_NAV_ITEMS, type AdminViewId } from './AdminSidebar';

export interface AdminHeaderProps {
  activeView: AdminViewId;
  className?: string;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({ activeView, className = '' }) => {
  const { t } = useI18n();
  const { adminUser, logout } = useAdminAuthStore();

  const currentNavItem = ADMIN_NAV_ITEMS.find((i) => i.id === activeView);
  const activeSophiaIcon = currentNavItem?.sophiaIcon ?? 'structure-server';

  const viewTitles: Record<AdminViewId, string> = {
    dashboard: 'admin.nav.dashboard',
    aiModels: 'admin.nav.aiModels',
    aiSandbox: 'admin.nav.aiSandbox',
    keypool: 'admin.nav.keypool',
    plans: 'admin.nav.plans',
    gateways: 'admin.nav.gateways',
    orders: 'admin.nav.orders',
    orderLogs: 'admin.nav.orderLogs',
    financial: 'admin.nav.financial',
    credits: 'admin.nav.credits',
    users: 'admin.nav.users',
    cmsBlog: 'admin.nav.cmsBlog',
    cmsPages: 'admin.nav.cmsPages',
    cmsNavigation: 'admin.nav.cmsNavigation',
    settings: 'admin.nav.settings',
    themeStudio: 'admin.nav.themeStudio',
    templateStudio: 'admin.nav.templateStudio',
    brandStudio: 'admin.nav.brandStudio',
    seedAssets: 'admin.nav.seedAssets',
    tokenAnalytics: 'admin.nav.tokenAnalytics',
    auditLogs: 'admin.nav.auditLogs',
  };

  return (
    <header className={`h-16 border-b border-surface-border bg-surface flex items-center justify-between px-6 select-none ${className}`}>
      {/* Title & Badge */}
      <div className="flex items-center gap-3">
        <div className="p-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 shrink-0">
          <SophiaIcon name={activeSophiaIcon} size={16} />
        </div>
        <h1 className="font-heading font-semibold text-base text-foreground">
          {t(viewTitles[activeView] || 'admin.title')}
        </h1>
        <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
          <SophiaIcon name="server" size={12} className="text-emerald-500 shrink-0" />
          <span>Edge Active</span>
        </span>
      </div>

      {/* Controls & Profile */}
      <div className="flex items-center gap-3">
        <LanguageSwitcher />
        <ThemeToggle />

        <div className="h-4 w-px bg-surface-border mx-1 hidden sm:block" />

        {/* Super Admin Info */}
        <div className="flex items-center gap-2.5 pl-1">
          <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-semibold text-xs">
            {adminUser?.fullName ? (
              adminUser.fullName.charAt(0).toUpperCase()
            ) : (
              <SophiaIcon name="finger-scanner" size={14} className="text-primary" />
            )}
          </div>
          <div className="hidden md:flex flex-col text-left leading-tight">
            <span className="text-xs font-medium text-foreground truncate max-w-[140px]">
              {adminUser?.fullName || 'Super Admin'}
            </span>
            <span className="text-[10px] text-muted-fg font-mono">
              {adminUser?.email || 'admin@sofia.edge'}
            </span>
          </div>

          <button
            type="button"
            onClick={logout}
            className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-rose-500/10 hover:border-rose-500/30 text-muted-fg hover:text-rose-500 transition-colors ml-1"
            title={t('auth.logout')}
            aria-label={t('auth.logout')}
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </header>
  );
};
